import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { db } from "@/lib/firebase";
import { doc, getDoc, collection, addDoc, setDoc, deleteDoc, query, where, getDocs, updateDoc } from "firebase/firestore";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  Heart, 
  Building2, 
  MapPin, 
  DollarSign, 
  TrendingUp, 
  Users, 
  Calendar,
  FileText,
  Mail,
  Phone,
  ExternalLink
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const ndaSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters").max(100),
  email: z.string().email("Invalid email address").max(255),
  companyName: z.string().min(2, "Company name must be at least 2 characters").max(100),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the NDA terms",
  }),
  signature: z.string().min(2, "Signature is required").max(100),
});

const contactSchema = z.object({
  subject: z.string().min(5, "Subject must be at least 5 characters").max(200),
  message: z.string().min(20, "Message must be at least 20 characters").max(2000),
  phoneNumber: z.string().optional(),
  preferredContactMethod: z.enum(["email", "phone", "both"]),
});

type NdaFormValues = z.infer<typeof ndaSchema>;
type ContactFormValues = z.infer<typeof contactSchema>;

interface Deal {
  id: string;
  title: string;
  industry: string;
  location: string;
  excerpt: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  revenue?: number;
  ebitda?: number;
  employees?: number;
  yearEstablished?: number;
  status: "active" | "pending" | "closed";
  strengths?: string[];
  weaknesses?: string[];
  opportunities?: string[];
  threats?: string[];
  keyMetrics?: {
    customerRetention?: number;
    growthRate?: number;
    profitMargin?: number;
  };
  createdAt: any;
  ownerId?: string;
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [hasSignedNda, setHasSignedNda] = useState(false);
  const [ndaDialogOpen, setNdaDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const ndaForm = useForm<NdaFormValues>({
    resolver: zodResolver(ndaSchema),
    defaultValues: {
      fullName: "",
      email: user?.email || "",
      companyName: "",
      agreeToTerms: false,
      signature: "",
    },
  });

  const contactForm = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      subject: "",
      message: "",
      phoneNumber: "",
      preferredContactMethod: "email",
    },
  });

  // Fetch deal details
  useEffect(() => {
    const fetchDeal = async () => {
      if (!id) return;
      
      try {
        const dealDoc = await getDoc(doc(db, "deals", id));
        if (dealDoc.exists()) {
          setDeal({ id: dealDoc.id, ...dealDoc.data() } as Deal);
        } else {
          toast({
            title: "Deal not found",
            description: "The deal you're looking for doesn't exist.",
            variant: "destructive",
          });
          navigate("/listings");
        }
      } catch (error) {
        console.error("Error fetching deal:", error);
        toast({
          title: "Error",
          description: "Failed to load deal details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDeal();
  }, [id, navigate, toast]);

  // Check if user has favorited this deal
  useEffect(() => {
    const checkFavorite = async () => {
      if (!user || !id) return;
      
      try {
        const favoriteDoc = await getDoc(doc(db, "favorites", `${user.id}_${id}`));
        setIsFavorite(favoriteDoc.exists());
      } catch (error) {
        console.error("Error checking favorite:", error);
      }
    };

    checkFavorite();
  }, [user, id]);

  // Check if user has signed NDA
  useEffect(() => {
    const checkNda = async () => {
      if (!user || !id) return;
      
      try {
        const ndaQuery = query(
          collection(db, "ndas"),
          where("userId", "==", user.id),
          where("dealId", "==", id)
        );
        const ndaDocs = await getDocs(ndaQuery);
        setHasSignedNda(!ndaDocs.empty);
      } catch (error) {
        console.error("Error checking NDA:", error);
      }
    };

    checkNda();
  }, [user, id]);

  const toggleFavorite = async () => {
    if (!user || !id) return;

    try {
      const favoriteRef = doc(db, "favorites", `${user.id}_${id}`);
      
      if (isFavorite) {
        await deleteDoc(favoriteRef);
        setIsFavorite(false);
        toast({
          title: "Removed from favorites",
          description: "Deal removed from your favorites.",
        });
      } else {
        await setDoc(favoriteRef, {
          userId: user.id,
          dealId: id,
          dealTitle: deal?.title,
          createdAt: new Date(),
        });
        setIsFavorite(true);
        toast({
          title: "Added to favorites",
          description: "Deal added to your favorites.",
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: "Error",
        description: "Failed to update favorites.",
        variant: "destructive",
      });
    }
  };

  const onSubmitNda = async (values: NdaFormValues) => {
    if (!user || !id) return;

    try {
      await addDoc(collection(db, "ndas"), {
        userId: user.id,
        dealId: id,
        dealTitle: deal?.title,
        ...values,
        signedAt: new Date(),
        status: "signed",
      });

      setHasSignedNda(true);
      setNdaDialogOpen(false);
      ndaForm.reset();
      
      toast({
        title: "NDA Signed",
        description: "You have successfully signed the NDA. You now have access to detailed information.",
      });
    } catch (error) {
      console.error("Error signing NDA:", error);
      toast({
        title: "Error",
        description: "Failed to sign NDA. Please try again.",
        variant: "destructive",
      });
    }
  };

  const onSubmitContact = async (values: ContactFormValues) => {
    if (!user || !id) return;

    try {
      await addDoc(collection(db, "contactRequests"), {
        fromUserId: user.id,
        fromUserEmail: user.email,
        dealId: id,
        dealTitle: deal?.title,
        toOwnerId: deal?.ownerId,
        ...values,
        status: "pending",
        createdAt: new Date(),
      });

      setContactDialogOpen(false);
      contactForm.reset();
      
      toast({
        title: "Message Sent",
        description: "Your message has been sent to the deal owner. They will contact you soon.",
      });
    } catch (error) {
      console.error("Error sending contact request:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return "Price on request";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(price);
  };

  const formatNumber = (num?: number) => {
    if (!num) return "N/A";
    return new Intl.NumberFormat("en-US").format(num);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!deal) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Deal Not Found</h2>
          <p className="text-muted-foreground mb-4">The deal you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/listings")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Deal Monitor
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Badge className={deal.status === "active" ? "bg-green-100 text-green-800" : ""}>
            {deal.status}
          </Badge>
        </div>

        {/* Hero Section */}
        <Card>
          <div className="relative h-64 w-full bg-muted rounded-t-lg overflow-hidden">
            {deal.imageUrl ? (
              <img
                src={deal.imageUrl}
                alt={deal.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Building2 className="h-24 w-24 text-muted-foreground" />
              </div>
            )}
          </div>
          <CardContent className="space-y-4 pt-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">{deal.title}</h1>
              <div className="flex flex-wrap gap-4 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>{deal.industry}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{deal.location}</span>
                </div>
                {deal.yearEstablished && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Est. {deal.yearEstablished}</span>
                  </div>
                )}
              </div>
            </div>

            <p className="text-lg">{deal.excerpt}</p>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4">
              <Button
                variant={isFavorite ? "default" : "outline"}
                onClick={toggleFavorite}
              >
                <Heart className={`mr-2 h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
                {isFavorite ? "Favorited" : "Add to Favorites"}
              </Button>

              <Dialog open={ndaDialogOpen} onOpenChange={setNdaDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" disabled={hasSignedNda}>
                    <FileText className="mr-2 h-4 w-4" />
                    {hasSignedNda ? "NDA Signed" : "Sign NDA"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Sign Non-Disclosure Agreement</DialogTitle>
                    <DialogDescription>
                      Please review and sign the NDA to access detailed information about this deal.
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...ndaForm}>
                    <form onSubmit={ndaForm.handleSubmit(onSubmitNda)} className="space-y-4">
                      <FormField
                        control={ndaForm.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <Input placeholder="John Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={ndaForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="john@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={ndaForm.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your Company Inc." {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
                        <h4 className="font-semibold">NDA Terms Summary</h4>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          <li>All information is confidential and proprietary</li>
                          <li>Information may not be disclosed to third parties</li>
                          <li>Information may only be used for evaluating this opportunity</li>
                          <li>Agreement is valid for 2 years from signing date</li>
                        </ul>
                      </div>

                      <FormField
                        control={ndaForm.control}
                        name="agreeToTerms"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                I agree to the terms and conditions of this NDA
                              </FormLabel>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={ndaForm.control}
                        name="signature"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Electronic Signature</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Type your full name as signature" 
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              By typing your name, you agree to sign this document electronically
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setNdaDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">Sign NDA</Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>

              <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Seller
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Contact Seller</DialogTitle>
                    <DialogDescription>
                      Send a message to the deal owner to express your interest.
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...contactForm}>
                    <form onSubmit={contactForm.handleSubmit(onSubmitContact)} className="space-y-4">
                      <FormField
                        control={contactForm.control}
                        name="subject"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subject</FormLabel>
                            <FormControl>
                              <Input placeholder="Interest in acquiring..." {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={contactForm.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="I'm interested in learning more about this opportunity..."
                                rows={6}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={contactForm.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="+1 (555) 123-4567" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={contactForm.control}
                        name="preferredContactMethod"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Preferred Contact Method</FormLabel>
                            <FormControl>
                              <select
                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                                {...field}
                              >
                                <option value="email">Email</option>
                                <option value="phone">Phone</option>
                                <option value="both">Both</option>
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setContactDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">Send Message</Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Asking Price</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPrice(deal.price)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Annual Revenue</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPrice(deal.revenue)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">EBITDA</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPrice(deal.ebitda)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Employees</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(deal.employees)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Information */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="swot">SWOT Analysis</TabsTrigger>
            <TabsTrigger value="metrics">Key Metrics</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Business Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {deal.description || "Detailed description available after signing NDA."}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="swot" className="space-y-4">
            {hasSignedNda ? (
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-green-600">Strengths</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {deal.strengths?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      )) || <li>Information available after NDA</li>}
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-600">Weaknesses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {deal.weaknesses?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      )) || <li>Information available after NDA</li>}
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-blue-600">Opportunities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {deal.opportunities?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      )) || <li>Information available after NDA</li>}
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-orange-600">Threats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-1">
                      {deal.threats?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      )) || <li>Information available after NDA</li>}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">NDA Required</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                    Sign the NDA to access detailed SWOT analysis and strategic information.
                  </p>
                  <Button onClick={() => setNdaDialogOpen(true)}>
                    <FileText className="mr-2 h-4 w-4" />
                    Sign NDA
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          <TabsContent value="metrics" className="space-y-4">
            {hasSignedNda ? (
              <Card>
                <CardHeader>
                  <CardTitle>Key Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {deal.keyMetrics?.customerRetention && (
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Customer Retention</span>
                        <span className="text-sm font-bold">{deal.keyMetrics.customerRetention}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${deal.keyMetrics.customerRetention}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {deal.keyMetrics?.growthRate && (
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Growth Rate</span>
                        <span className="text-sm font-bold">{deal.keyMetrics.growthRate}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${Math.min(deal.keyMetrics.growthRate, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {deal.keyMetrics?.profitMargin && (
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Profit Margin</span>
                        <span className="text-sm font-bold">{deal.keyMetrics.profitMargin}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${deal.keyMetrics.profitMargin}%` }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">NDA Required</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                    Sign the NDA to access detailed performance metrics and financial data.
                  </p>
                  <Button onClick={() => setNdaDialogOpen(true)}>
                    <FileText className="mr-2 h-4 w-4" />
                    Sign NDA
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
